import { ResourceBaseService } from "@/core/common/resource-base/resource-base.service";
import { UserHasQualificationDto } from "@/core/features/users-have-qualifications/user-has-qualification.dto";
import { CreateUserHasQualificationDto } from "@/core/features/users-have-qualifications/create-user-has-qualification.dto";
import { UpdateUserHasQualificationDto } from "@/core/features/users-have-qualifications/update-user-has-qualification.dto";
import { UpdateUserHasQualificationAccountingDto } from "@/core/features/users-have-qualifications/update-user-has-qualification-accounting.dto";
import { ApiHttpClient } from "@/core/common/http/api-http-client";
import { IRequest } from "@/interfaces/IRequest";
import { HttpRequest } from "@/core/common/http/http-request";
import { plainToInstance } from "class-transformer";
import { QualificationTotalsDto } from "@/core/features/qualifications/qualification-totals.dto";
import { UserDto } from "@/core/features/users/user.dto";
import { FindManyResult } from "@/core/common/resource-base/find-many-result.dto";
import { BulkCreateUserHasQualificationsDto } from "@/core/features/users-have-qualifications/bulk-create-user-has-qualifications.dto";
import { BulkCreateUserHasQualificationsResultDto } from "@/core/features/users-have-qualifications/bulk-create-user-has-qualifications-result.dto";
import { UpdateUserHasQualificationStatusBulkDto } from "@/core/features/users-have-qualifications/update-user-has-qualification-status-bulk.dto";
import { applyOptionLoaderPagination } from "@/core/common/presenters/optionLoader";
import { OptionType } from "@/types/OptionType";
import { UserHasQualificationStatusEnum } from "@altertec_gparn/lib";
import { PreviewUserHasQualificationsResultDto } from "./preview-user-has-qualifications-result.dto";

export class UsersHaveQualificationsResource extends ResourceBaseService<
  UserHasQualificationDto,
  CreateUserHasQualificationDto,
  UpdateUserHasQualificationDto
> {
  protected EntityDto = UserHasQualificationDto;
  protected CreateEntityDto = CreateUserHasQualificationDto;
  protected UpdateEntityDto = UpdateUserHasQualificationDto;

  constructor(protected readonly apiHttpClient: ApiHttpClient) {
    super(apiHttpClient, "/users-have-qualifications");
  }

  async getCostTotal(request: IRequest): Promise<QualificationTotalsDto> {
    return this.apiHttpClient
      .request(
        HttpRequest.create({
          url: this.resourceUrl + "/total",
          method: "GET",
          auth: true,
          query: this.getQueryBuilder(request).query(),
        })
      )
      .then((result) => plainToInstance(QualificationTotalsDto, result));
  }

  async getEligibleUsers(qualificationId: string, request?: IRequest): Promise<FindManyResult<UserDto>> {
    const baseQuery = this.getQueryBuilder(request).query();
    const query = baseQuery
      ? `${baseQuery}&qualificationId=${encodeURIComponent(qualificationId)}`
      : `qualificationId=${encodeURIComponent(qualificationId)}`;

    return this.apiHttpClient
      .request<null, FindManyResult<UserDto>>(
        HttpRequest.create({
          url: `${this.resourceUrl}/eligible-users`,
          method: "GET",
          auth: true,
          query,
        })
      )
      .then((findManyResult) => {
        findManyResult.data = plainToInstance(UserDto, findManyResult.data);
        return findManyResult;
      });
  }

  async getEligibleUsersOptionLoader(qualificationId: string, inputValue: string): Promise<OptionType[]> {
    const request: IRequest = inputValue
      ? { search: inputValue, searchBy: ["id", "username", "firstName", "lastName"] }
      : {};
    request.sortBy = "username";
    applyOptionLoaderPagination(request);
    const { data } = await this.getEligibleUsers(qualificationId, request);
    return data.map((user) => user.mapEntityToOptionType([user.username]));
  }

  async bulkCreate(body: BulkCreateUserHasQualificationsDto): Promise<BulkCreateUserHasQualificationsResultDto> {
    return this.apiHttpClient
      .request<BulkCreateUserHasQualificationsDto, BulkCreateUserHasQualificationsResultDto>(
        HttpRequest.create({
          url: `${this.resourceUrl}/bulk`,
          method: "POST",
          auth: true,
          body: plainToInstance(BulkCreateUserHasQualificationsDto, body, { excludeExtraneousValues: true }),
        })
      )
      .then((result) => plainToInstance(BulkCreateUserHasQualificationsResultDto, result));
  }

  async previewBulkCreate(body: BulkCreateUserHasQualificationsDto): Promise<PreviewUserHasQualificationsResultDto> {
    return this.apiHttpClient
      .request<BulkCreateUserHasQualificationsDto, PreviewUserHasQualificationsResultDto>(
        HttpRequest.create({
          url: `${this.resourceUrl}/bulk/preview`,
          method: "POST",
          auth: true,
          body: plainToInstance(BulkCreateUserHasQualificationsDto, body, { excludeExtraneousValues: true }),
        })
      )
      .then((result) => plainToInstance(PreviewUserHasQualificationsResultDto, result));
  }

  async updateAccounting(id: string, body: UpdateUserHasQualificationAccountingDto): Promise<UserHasQualificationDto> {
    return this.apiHttpClient
      .request<UpdateUserHasQualificationAccountingDto, UserHasQualificationDto>(
        HttpRequest.create({
          url: `${this.resourceUrl}/${id}`,
          method: "PATCH",
          auth: true,
          body: plainToInstance(UpdateUserHasQualificationAccountingDto, body, { excludeExtraneousValues: true }),
        })
      )
      .then((entity) => plainToInstance(UserHasQualificationDto, entity));
  }

  async archiveQualification(id: string): Promise<UserHasQualificationDto> {
    return this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/${id}/archive`,
        method: "PATCH",
        auth: true,
        body: {},
      })
    );
  }

  async renewQualification(id: string, body: UpdateUserHasQualificationDto): Promise<UserHasQualificationDto> {
    return this.transitionQualification(id, "renew", body);
  }

  async recycleQualification(id: string, body: UpdateUserHasQualificationDto): Promise<UserHasQualificationDto> {
    return this.transitionQualification(id, "recycle", body);
  }

  private async transitionQualification(
    id: string,
    action: "renew" | "recycle",
    body: UpdateUserHasQualificationDto
  ): Promise<UserHasQualificationDto> {
    return this.apiHttpClient
      .request<UpdateUserHasQualificationDto, UserHasQualificationDto>(
        HttpRequest.create({
          url: `${this.resourceUrl}/${id}/${action}`,
          method: "POST",
          auth: true,
          body: plainToInstance(UpdateUserHasQualificationDto, body, { excludeExtraneousValues: true }),
        })
      )
      .then((result) => plainToInstance(UserHasQualificationDto, result));
  }

  async updateStatusInBulk(status: UserHasQualificationStatusEnum, ids: string[]): Promise<void> {
    await this.apiHttpClient.request(
      HttpRequest.create({
        url: `${this.resourceUrl}/bulk/change-status`,
        method: "PATCH",
        auth: true,
        body: plainToInstance(UpdateUserHasQualificationStatusBulkDto, { status, ids }),
      })
    );
  }

  async importUserHasQualifications(file: File, confirmRenewals = false): Promise<{ jobId: string }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("confirmRenewals", String(confirmRenewals));

    return this.apiHttpClient.request<FormData, { jobId: string }>(
      HttpRequest.create<FormData>({
        url: `${this.resourceUrl}/import`,
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );
  }

  async previewUserHasQualificationsImport(file: File): Promise<{ jobId: string }> {
    const formData = new FormData();
    formData.append("file", file);

    return this.apiHttpClient.request<FormData, { jobId: string }>(
      HttpRequest.create<FormData>({
        url: `${this.resourceUrl}/import/preview`,
        method: "POST",
        auth: true,
        body: formData,
        type: "form-data",
      })
    );
  }

  async downloadImportTemplate(): Promise<void> {
    return this.downloadExcel(undefined, "/import/template", "plantilla-formaciones-tecnico");
  }
}
